import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Crown, Zap, Check, X } from "lucide-react";

export function UpgradeModal({ isOpen, onClose, feature, currentPlan, limit, current }) {
  const navigate = useNavigate();

  const features = {
    systems: {
      title: "Límite de Sistemas Alcanzado",
      description: `Has alcanzado el límite de ${limit} sistemas en tu plan ${currentPlan}.`,
      icon: "📡"
    },
    events: {
      title: "Límite de Eventos Alcanzado", 
      description: `Has alcanzado el límite de ${limit} eventos en tu plan ${currentPlan}.`,
      icon: "📅"
    },
    hardware: {
      title: "Conexión Hardware USB",
      description: "La conexión directa con analizadores RF via USB requiere un plan Pro o Enterprise.",
      icon: "🔌"
    },
    pdf_reports: {
      title: "Reportes PDF",
      description: "La generación de reportes PDF profesionales requiere un plan Pro o Enterprise.",
      icon: "📄"
    }
  };

  const featureInfo = features[feature] || {
    title: "Función Premium",
    description: "Esta función requiere un plan superior.",
    icon: "⭐"
  };

  const plans = [
    {
      name: "Pro",
      price: "$9.990",
      period: "/mes",
      features: [
        "25 sistemas IEM",
        "15 eventos guardados",
        "Conexión hardware USB",
        "Reportes PDF",
        "Alertas RF completas",
        "Soporte por email"
      ],
      recommended: true
    },
    {
      name: "Enterprise",
      price: "$24.990",
      period: "/mes",
      features: [
        "Sistemas ilimitados",
        "Eventos ilimitados",
        "Conexión hardware USB",
        "Reportes PDF",
        "3 usuarios por cuenta",
        "Soporte prioritario"
      ],
      recommended: false
    }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#111111] border-white/10 max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-xl">
            <span className="text-2xl">{featureInfo.icon}</span>
            <span className="text-white">{featureInfo.title}</span>
          </DialogTitle>
          <DialogDescription className="text-[#A1A1AA] pt-2">
            {featureInfo.description}
          </DialogDescription>
        </DialogHeader>

        {/* Current usage indicator */}
        {current !== undefined && limit !== undefined && (
          <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#71717A]">Uso actual</span>
              <span className="text-sm font-mono text-white">{current} / {limit}</span>
            </div>
            <div className="w-full bg-[#1A1A1A] rounded-full h-2">
              <div 
                className="bg-[#FF3B30] h-2 rounded-full transition-all"
                style={{ width: `${Math.min((current / limit) * 100, 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Upgrade options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          {plans.map((plan) => (
            <div 
              key={plan.name}
              className={`relative p-5 rounded-lg border ${
                plan.recommended 
                  ? "border-[#34C759] bg-[#34C759]/5" 
                  : "border-white/10 bg-[#0A0A0A]"
              }`}
            >
              {plan.recommended && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-[#34C759] text-black text-xs font-bold px-3 py-1 rounded-full">
                    RECOMENDADO
                  </span>
                </div>
              )}
              
              <div className="text-center mb-4 pt-2">
                <h3 className="text-lg font-bold text-white">{plan.name}</h3>
                <div className="mt-2">
                  <span className="text-2xl font-bold text-[#34C759]">{plan.price}</span>
                  <span className="text-[#71717A] text-sm">{plan.period}</span>
                </div>
              </div>

              <ul className="space-y-2 mb-4">
                {plan.features.map((feat, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                    <Check className="w-4 h-4 text-[#34C759] flex-shrink-0" />
                    {feat}
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => {
                  onClose();
                  navigate("/subscription");
                }}
                className={`w-full ${
                  plan.recommended
                    ? "bg-[#34C759] hover:bg-[#34C759]/90 text-black"
                    : "bg-white/10 hover:bg-white/20 text-white"
                }`}
              >
                <Crown className="w-4 h-4 mr-2" />
                Actualizar a {plan.name}
              </Button>
            </div>
          ))}
        </div>

        {/* Close button */}
        <div className="flex justify-center mt-4">
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-[#71717A] hover:text-white"
          >
            Continuar con plan actual
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default UpgradeModal;
